import React from "react";

interface MetaPolicyStruct {
  [key: string]: any;
}

interface Props {
  struct: MetaPolicyStruct;
}

const IncludedHotelServicesSection: React.FC<Props> = ({ struct }) => {
  const included = {
    cot: struct.cot?.filter((item: any) => item.inclusion === "included") || [],
    internet: struct.internet?.filter((item: any) => item.inclusion === "included") || [],
    shuttle: struct.shuttle?.filter((item: any) => item.inclusion === "included") || [],
    meal: struct.meal?.filter((item: any) => item.inclusion === "included") || [],
    children_meal: struct.children_meal?.filter((item: any) => item.inclusion === "included") || [],
    parking: struct.parking?.filter((item: any) => item.inclusion === "included") || [],
    pets: struct.pets?.filter((item: any) => item.inclusion === "included") || [],
    extra_bed: struct.extra_bed?.filter((item: any) => item.inclusion === "included") || [],
    deposit: struct.deposit?.filter((item: any) => item.inclusion === "included") || [],
    add_fee: struct.add_fee?.filter((item: any) => item.inclusion === "included") || [],
    check_in_check_out: struct.check_in_check_out?.filter((item: any) => item.inclusion === "included") || [],
  };

  const hasAny =
    Object.values(included).reduce((acc, arr) => acc + arr.length, 0) > 0;

  if (!hasAny) return null;

  return (
    <div className="mt-12 mx-4 md:mx-0 bg-white shadow-md rounded-lg border border-gray-200">
      <div className="bg-gray-100 p-4 rounded-t-lg">
        <h2 className="text-lg font-semibold text-gray-900">Hotel services</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 text-sm text-gray-800">
        {/* Cot */}
        {included.cot.map((item: any, i: number) => (
          <ServiceBox
            key={`cot-${i}`}
            icon="baby-bed"
            title="Cradle"
            fields={[
              { label: "Maximum number", value: item.amount || "Needs to be clarified on the spot" },
              { label: "Price", value: "Free" },
            ]}
          />
        ))}

        {/* Internet */}
        {included.internet.map((item: any, i: number) => (
          <ServiceBox
            key={`internet-${i}`}
            icon="internet"
            title="Internet"
            fields={[
              { label: "Location", value: item.work_area === "room" ? "In the room" : "In the lobby" },
              { label: "Price", value: "Free" },
            ]}
          />
        ))}

        {/* Shuttle */}
        {included.shuttle.map((item: any, i: number) => (
          <ServiceBox
            key={`shuttle-${i}`}
            icon="transfer"
            title="Transfer"
            fields={[
              {
                label: "Destination",
                value: item.destination_type === "airport_train"
                  ? "Airport shuttle train"
                  : item.destination_type,
              },
              { label: "Price", value: "Free" },
            ]}
          />
        ))}

        {/* Meals */}
        {included.meal.map((item: any, i: number) => (
          <ServiceBox
            key={`meal-${i}`}
            icon="meals"
            title={`Meal - ${capitalize(item.meal_type)}`}
            fields={[{ label: "Price", value: "Free" }]}
          />
        ))}

        {/* Children Meals */}
        {included.children_meal.map((item: any, i: number) => (
          <ServiceBox
            key={`childmeal-${i}`}
            icon="meals"
            title="Children Meal"
            fields={[
              {
                label: "Age range",
                value: `${item.age_start}–${item.age_end}`,
              },
              { label: "Meal", value: item.meal_type },
              { label: "Price", value: "Free" },
            ]}
          />
        ))}

        {/* Parking */}
        {included.parking.map((item: any, i: number) => (
          <ServiceBox
            key={`parking-${i}`}
            icon="parking"
            title="Parking"
            fields={[
              { label: "Location", value: item.terrritory_type !== 'unspecified' ? item.terrritory_type : "On site" },
              { label: "Price", value: "Free" },
            ]}
          />
        ))}

        {/* Pets */}
        {included.pets.map((item: any, i: number) => (
          <ServiceBox
            key={`pets-${i}`}
            icon="pets"
            title="Pets"
            fields={[
              { label: "Availability", value: item.pets_type !== 'unspecified' ? item.pets_type : "Pets allowed" },
              { label: "Price", value: "Free" },
            ]}
          />
        ))}

        {/* Extra Bed */}
        {included.extra_bed.map((item: any, i: number) => (
          <ServiceBox
            key={`bed-${i}`}
            icon="bed"
            title="Extra bed"
            fields={[
              { label: "Maximum number", value: `${item.amount}` },
              { label: "Price", value: "Free" },
            ]}
          />
        ))}

        {/* Deposit */}
        {included.deposit.map((item: any, i: number) => (
          <ServiceBox
            key={`deposit-${i}`}
            icon="deposit"
            title="Deposit"
            fields={[{ label: "Type", value: item.deposit_type || "Unspecified" }, { label: "Price", value: "Free" }]}
          />
        ))}

        {/* Additional Services */}
        {included.add_fee.map((item: any, i: number) => (
          <ServiceBox
            key={`addfee-${i}`}
            icon="money"
            title="Additional Service"
            fields={[
              { label: "Service", value: item.fee_type },
              { label: "Price", value: "Free" },
            ]}
          />
        ))}

        {/* Check-in/Check-out */}
        {included.check_in_check_out.map((item: any, i: number) => (
          <ServiceBox
            key={`checkin-${i}`}
            icon="checkin"
            title="Check-in/Check-out"
            fields={[
              {
                label: "Type",
                value: capitalize(item.check_in_check_out_type),
              },
              { label: "Price", value: "Free" },
            ]}
          />
        ))}
      </div>
    </div>
  );
};

const ServiceBox = ({
  icon,
  title,
  fields,
}: {
  icon: string;
  title: string;
  fields: { label: string; value: string }[];
}) => (
  <div>
    <div className="font-semibold mb-1 flex items-center gap-2">
      <img src={`/icons/${icon}.svg`} alt={`${icon} icon`} className="w-4 h-4" />
      {title}
    </div>
    <div className="text-gray-600">
      {fields.map((f, i) => (
        <div key={i}>
          <span className="font-medium">{f.label}:</span> {f.value}
        </div>
      ))}
    </div>
  </div>
);

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export default IncludedHotelServicesSection;
